import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Settings as SettingsIcon, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRule, setNewRule] = useState({ metric: 'SPI', operator: '<', value: '', severity: 'warning', channel: 'in_app' });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/portfolios/portfolio_001/thresholds');
        setThresholds(res.data);
      } catch { toast.error('Failed to load thresholds'); }
      finally { setLoading(false); }
    })();
  }, []);

  const addThreshold = async () => {
    if (!newRule.value) return toast.error('Please enter a threshold value');
    try {
      const res = await api.post('/portfolios/portfolio_001/thresholds', {
        ...newRule, value: parseFloat(newRule.value)
      });
      setThresholds(prev => [...prev, res.data]);
      setNewRule({ metric: 'SPI', operator: '<', value: '', severity: 'warning', channel: 'in_app' });
      toast.success('Threshold rule created');
    } catch { toast.error('Failed to create threshold'); }
  };

  const toggleRule = async (ruleId, active) => {
    try {
      await api.put(`/portfolios/portfolio_001/thresholds/${ruleId}`, { active: !active });
      setThresholds(prev => prev.map(t => t.rule_id === ruleId ? { ...t, active: !active } : t));
    } catch { toast.error('Failed to update rule'); }
  };

  if (loading) return <DashboardLayout><div className="h-96 loading-shimmer rounded-xl" /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-white" data-testid="settings-title">Settings & Configuration</h2>
        <p className="text-slate-500 text-sm">Manage threshold rules, alert channels, and portfolio configuration</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Threshold Rules */}
        <div className="lg:col-span-8 space-y-4">
          <div className="glass-card p-5" data-testid="threshold-settings">
            <h3 className="font-heading text-base font-semibold text-white mb-4 flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-[#1E4E8C]" /> Threshold Alert Rules
            </h3>

            {/* Add New Rule */}
            <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.05] mb-4">
              <h4 className="text-xs text-slate-400 uppercase tracking-wider mb-3">Add New Rule</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase">Metric</Label>
                  <Select value={newRule.metric} onValueChange={(v) => setNewRule({ ...newRule, metric: v })}>
                    <SelectTrigger data-testid="new-rule-metric" className="bg-[#0A0E1A]/50 border-[#3A3F4A] text-xs h-8 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      {['SPI', 'CPI', 'HI Score', 'Risk Exposure', 'Schedule Variance', 'Cost Variance'].map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase">Operator</Label>
                  <Select value={newRule.operator} onValueChange={(v) => setNewRule({ ...newRule, operator: v })}>
                    <SelectTrigger data-testid="new-rule-operator" className="bg-[#0A0E1A]/50 border-[#3A3F4A] text-xs h-8 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      <SelectItem value="<">Less than</SelectItem>
                      <SelectItem value=">">Greater than</SelectItem>
                      <SelectItem value="<=">Less or equal</SelectItem>
                      <SelectItem value=">=">Greater or equal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase">Value</Label>
                  <Input data-testid="new-rule-value" type="number" step="0.01" value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                    className="bg-[#0A0E1A]/50 border-[#3A3F4A] text-xs h-8 mt-1" placeholder="0.85" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500 uppercase">Severity</Label>
                  <Select value={newRule.severity} onValueChange={(v) => setNewRule({ ...newRule, severity: v })}>
                    <SelectTrigger data-testid="new-rule-severity" className="bg-[#0A0E1A]/50 border-[#3A3F4A] text-xs h-8 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button data-testid="add-threshold-btn" onClick={addThreshold}
                    className="bg-[#1E4E8C] hover:bg-[#143A6B] transition-colors font-bold uppercase tracking-wider text-[10px] h-8 w-full rounded-full text-white">
                    <Plus className="w-3 h-3 mr-1" /> Add Rule
                  </Button>
                </div>
              </div>
            </div>

            {/* Existing Rules */}
            <div className="space-y-2">
              {thresholds.map((t, i) => (
                <motion.div key={t.rule_id || i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="flex items-center justify-between p-3 rounded-md bg-white/[0.02] border border-white/[0.03]">
                  <div className="flex items-center gap-3">
                    <Switch
                      data-testid={`toggle-rule-${t.rule_id}`}
                      checked={t.active !== false}
                      onCheckedChange={() => toggleRule(t.rule_id, t.active !== false)}
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white font-medium">{t.metric}</span>
                        <span className="text-[10px] text-slate-500 font-mono">{t.operator} {t.value}</span>
                      </div>
                      <span className="text-[10px] text-slate-600">{t.channel}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[9px] ${t.severity === 'critical' ? 'text-red-400 border-red-500/20' : t.severity === 'warning' ? 'text-[#5790C9] border-[#1E4E8C]/20' : 'text-[#5790C9] border-sky-500/20'}`}>
                    {t.severity}
                  </Badge>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="lg:col-span-4 space-y-4">
          <div className="glass-card p-4">
            <h3 className="font-heading text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">Health Index Weights</h3>
            <div className="space-y-3">
              {[
                { label: 'Schedule (w1)', value: '0.30', color: 'bg-[#1E4E8C]' },
                { label: 'Cost (w2)', value: '0.25', color: 'bg-[#1E4E8C]' },
                { label: 'Risk (w3)', value: '0.25', color: 'bg-red-500' },
                { label: 'Quality (w4)', value: '0.10', color: 'bg-emerald-500' },
                { label: 'Safety (w5)', value: '0.10', color: 'bg-purple-500' },
              ].map((w, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${w.color}`} />
                    <span className="text-xs text-slate-400">{w.label}</span>
                  </div>
                  <span className="font-mono text-xs text-white">{w.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-4">
            <h3 className="font-heading text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">RAG Thresholds</h3>
            <div className="space-y-3">
              {[
                { label: 'Green', condition: 'HI >= 80', color: 'bg-emerald-500' },
                { label: 'Amber', condition: '65 <= HI < 80', color: 'bg-[#1E4E8C]' },
                { label: 'Red', condition: 'HI < 65', color: 'bg-red-500' },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${r.color}`} />
                    <span className="text-xs text-slate-400">{r.label}</span>
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">{r.condition}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-4">
            <h3 className="font-heading text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wider">Platform Info</h3>
            <div className="space-y-2 text-[10px] text-slate-500">
              <div className="flex justify-between"><span>Version</span><span className="text-slate-300">1.0.0</span></div>
              <div className="flex justify-between"><span>AI Model</span><span className="text-[#5790C9]">Cognara</span></div>
              <div className="flex justify-between"><span>Database</span><span className="text-slate-300">MongoDB</span></div>
              <div className="flex justify-between"><span>Refresh Rate</span><span className="text-slate-300">5 min</span></div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
